import { useState } from 'react';
import type { PollOption } from '@/mocks/gossip';

interface PollWidgetProps {
  question: string;
  options: PollOption[];
  compact?: boolean;
}

export default function PollWidget({ question, options, compact = false }: PollWidgetProps) {
  const [votes, setVotes] = useState<PollOption[]>(options);
  const [selected, setSelected] = useState<string | null>(null);
  const [voted, setVoted] = useState(false);

  const total = votes.reduce((sum, option) => sum + option.votes, 0);

  const handleVote = () => {
    if (!selected || voted) return;
    setVotes((prev) => prev.map((o) => (o.id === selected ? { ...o, votes: o.votes + 1 } : o)));
    setVoted(true);
  };

  const percent = (value: number) => (total === 0 ? 0 : Math.round((value / total) * 100));

  return (
    <div className={`rounded-lg bg-background-50 border border-background-200 ${compact ? 'p-4' : 'p-5 md:p-6'}`}>
      <div className="flex items-center gap-2 text-primary-600 text-xs mb-2">
        <i className="ri-bar-chart-2-line"></i>
        粉丝投票 · 一人一票
      </div>
      <h3 className={`font-heading text-foreground-950 ${compact ? 'text-base' : 'text-xl'}`}>{question}</h3>

      <div className={`mt-4 space-y-2.5 ${voted ? 'pointer-events-none' : ''}`}>
        {votes.map((option) => {
          const pct = percent(option.votes);
          const isSelected = selected === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => setSelected(option.id)}
              disabled={voted}
              aria-pressed={isSelected}
              className={`relative w-full text-left rounded-md border px-3 py-2.5 overflow-hidden transition-colors cursor-pointer ${
                isSelected ? 'border-primary-400' : 'border-background-300 hover:border-primary-300'
              }`}
            >
              {voted && (
                <span
                  className={`absolute inset-y-0 left-0 ${
                    isSelected ? 'bg-primary-100' : 'bg-background-200'
                  }`}
                  style={{ width: `${pct}%` }}
                ></span>
              )}
              <span className="relative flex items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm text-foreground-800">
                  <i className={isSelected ? 'ri-radio-button-line text-primary-600' : 'ri-checkbox-blank-circle-line text-foreground-300'}></i>
                  {option.label}
                </span>
                {voted && <span className="text-xs text-foreground-600 shrink-0">{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="text-xs text-foreground-500">已参与 {total.toLocaleString()} 人</span>
        {voted ? (
          <span className="text-xs text-secondary-700 flex items-center gap-1">
            <i className="ri-checkbox-circle-line"></i>
            投票成功，结果已更新
          </span>
        ) : (
          <button
            type="button"
            onClick={handleVote}
            disabled={!selected}
            className={`px-4 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
              selected
                ? 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                : 'bg-background-200 text-foreground-400 cursor-not-allowed'
            }`}
          >
            提交投票
          </button>
        )}
      </div>
    </div>
  );
}